import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Clock, MapPin, Phone, User, FileText, Check, Truck, Package, ArrowRight, Loader2, Map } from 'lucide-react';

const DeliveryMap = lazy(() => import('../components/DeliveryMap'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'booked', label: 'Booked', icon: Check },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
  { key: 'picked_up', label: 'Picked Up', icon: Package },
  { key: 'in_transit', label: 'In Transit', icon: ArrowRight },
  { key: 'arriving', label: 'Arriving', icon: MapPin },
  { key: 'delivered', label: 'Delivered', icon: Check },
];

const TrackDelivery = () => {
  const [jobId, setJobId] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [liveLocation, setLiveLocation] = useState(null);

  // Poll GPS location every 15 seconds when delivery is active
  const fetchLocation = useCallback(async () => {
    if (!delivery || !delivery.identifier_used || delivery.status === 'delivered' || delivery.status === 'booked') return;
    try {
      const res = await axios.get(`${API}/track/${delivery.id}/location?identifier=${encodeURIComponent(delivery.identifier_used)}`);
      if (res.data.has_location) {
        setLiveLocation(res.data);
      }
    } catch {
      // Silent fail for location polling
    }
  }, [delivery]);

  useEffect(() => {
    if (!delivery || delivery.status === 'delivered' || delivery.status === 'booked') return;
    fetchLocation();
    const interval = setInterval(fetchLocation, 15000);
    return () => clearInterval(interval);
  }, [delivery, fetchLocation]);

  useEffect(() => {
    if (!delivery?.eta) { setCountdown(null); return; }
    const etaDate = new Date(delivery.eta);
    if (isNaN(etaDate.getTime())) { setCountdown(null); return; }
    const update = () => {
      const diff = etaDate - new Date();
      if (diff <= 0) { setCountdown(null); return; }
      setCountdown({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [delivery?.eta]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!jobId.trim() || !identifier.trim()) return;
    setError('');
    setDelivery(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/track`, { job_id: jobId.trim().toUpperCase(), identifier: identifier.trim() });
      setDelivery(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Delivery not found. Please check your information.');
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = delivery ? STAGES.findIndex(s => s.key === delivery.status) : -1;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen" data-testid="track-delivery-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-red/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              TRACK YOUR <span className="text-brand-red">DELIVERY</span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg mb-10">
              Enter your Job ID and phone number or last name to see real-time updates on your delivery.
            </p>

            {/* Search Form */}
            <form onSubmit={handleTrack} className="space-y-4" data-testid="tracking-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Job ID (e.g. TF-50274)"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-all text-base"
                    data-testid="tracking-job-id-input"
                    required
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Phone number or last name"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-all text-base"
                    data-testid="tracking-identifier-input"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-10 py-4 bg-brand-red hover:bg-red-700 text-white font-heading tracking-wide rounded-xl transition-all flex items-center justify-center gap-3 mx-auto disabled:opacity-50"
                data-testid="tracking-submit-button"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                {loading ? 'SEARCHING...' : 'TRACK DELIVERY'}
              </button>
            </form>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"
                data-testid="tracking-error"
              >
                {error}
              </motion.div>
            )}

            <p className="mt-6 text-slate-500 text-sm">
              Looking for all your deliveries?{' '}
              <Link to="/my-deliveries" className="text-brand-red hover:text-red-400 transition-colors font-medium" data-testid="portal-link-from-track">
                View delivery history
              </Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tracking Results */}
      {delivery && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
            data-testid="tracking-results"
          >
            {/* Job ID Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <span className="text-slate-500 text-sm">Tracking</span>
                <h2 className="font-heading text-3xl text-white">{delivery.id}</h2>
              </div>
              <div className="text-right">
                <span className="text-slate-500 text-xs">Last updated</span>
                <p className="text-slate-300 text-sm">{formatDate(delivery.updated_at)}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-brand-surface rounded-2xl border border-white/10 p-6 sm:p-8 mb-6" data-testid="tracking-progress">
              <div className="flex items-center justify-between relative">
                {/* Connection line */}
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/10" />
                <div
                  className="absolute top-5 left-0 h-0.5 bg-brand-red transition-all duration-700"
                  style={{ width: currentIdx >= 0 ? `${(currentIdx / (STAGES.length - 1)) * 100}%` : '0%' }}
                />
                {STAGES.map((stage, i) => {
                  const Icon = stage.icon;
                  const isCompleted = i < currentIdx;
                  const isCurrent = i === currentIdx;
                  const isUpcoming = i > currentIdx;
                  return (
                    <div key={stage.key} className="flex flex-col items-center relative z-10" data-testid={`stage-${stage.key}`}>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                          isCompleted ? 'bg-brand-red border-brand-red text-white' :
                          isCurrent ? 'bg-brand-red border-brand-red text-white ring-4 ring-brand-red/20' :
                          'bg-brand-surface border-white/20 text-slate-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`mt-2 text-xs font-medium text-center hidden sm:block ${
                        isCompleted || isCurrent ? 'text-white' : 'text-slate-600'
                      }`}>
                        {stage.label}
                      </span>
                      {isCurrent && (
                        <span className="sm:hidden mt-2 text-xs font-medium text-brand-red">{stage.label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Current status label on mobile */}
              <div className="sm:hidden mt-4 text-center">
                <span className="text-slate-500 text-xs">Current Status</span>
                <p className="font-heading text-lg text-white">
                  {STAGES[currentIdx]?.label || delivery.status}
                </p>
              </div>
            </div>

            {/* Info Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* ETA Card */}
              <div className="bg-brand-surface rounded-2xl border border-white/10 p-6" data-testid="eta-card">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-brand-red" />
                  <span className="text-slate-400 text-sm font-medium">Estimated Arrival</span>
                </div>
                {delivery.status === 'delivered' ? (
                  <p className="font-heading text-xl text-green-400">DELIVERED</p>
                ) : delivery.eta ? (
                  <>
                    <p className="text-white text-lg mb-2">{formatDate(delivery.eta)}</p>
                    {countdown && (
                      <div className="flex gap-3" data-testid="eta-countdown">
                        <div className="bg-white/5 rounded-lg px-3 py-2 text-center min-w-[60px]">
                          <span className="font-heading text-2xl text-brand-red">{String(countdown.hours).padStart(2, '0')}</span>
                          <p className="text-slate-500 text-xs mt-0.5">hrs</p>
                        </div>
                        <div className="bg-white/5 rounded-lg px-3 py-2 text-center min-w-[60px]">
                          <span className="font-heading text-2xl text-white">{String(countdown.minutes).padStart(2, '0')}</span>
                          <p className="text-slate-500 text-xs mt-0.5">min</p>
                        </div>
                        <div className="bg-white/5 rounded-lg px-3 py-2 text-center min-w-[60px]">
                          <span className="font-heading text-2xl text-white">{String(countdown.seconds).padStart(2, '0')}</span>
                          <p className="text-slate-500 text-xs mt-0.5">sec</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-slate-500">ETA not yet available</p>
                )}
              </div>

              {/* Driver Card */}
              <div className="bg-brand-surface rounded-2xl border border-white/10 p-6" data-testid="driver-card">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-brand-blue" />
                  <span className="text-slate-400 text-sm font-medium">Driver Information</span>
                </div>
                {delivery.driver_name ? (
                  <div>
                    <p className="text-white text-lg font-medium">{delivery.driver_name}</p>
                    {delivery.driver_phone && (
                      <a
                        href={`tel:${delivery.driver_phone}`}
                        className="inline-flex items-center gap-2 mt-2 text-brand-blue hover:text-blue-300 transition-colors"
                        data-testid="driver-phone-link"
                      >
                        <Phone className="w-4 h-4" />
                        {delivery.driver_phone}
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500">Driver info will appear when assigned</p>
                )}
              </div>
            </div>

            {/* Locations */}
            <div className="bg-brand-surface rounded-2xl border border-white/10 p-6 mb-6" data-testid="locations-card">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="text-slate-500 text-xs uppercase tracking-wider">Pickup</span>
                  </div>
                  <p className="text-white">{delivery.pickup_address}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-brand-red" />
                    <span className="text-slate-500 text-xs uppercase tracking-wider">Delivery</span>
                  </div>
                  <p className="text-white">{delivery.delivery_address}</p>
                </div>
              </div>
            </div>

            {/* Live Map */}
            {(liveLocation || (delivery.driver_lat && delivery.driver_lng)) && (
              <div className="bg-brand-surface rounded-2xl border border-white/10 p-6 mb-6" data-testid="live-map-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Map className="w-5 h-5 text-brand-red" />
                    <span className="text-white text-sm font-medium">Live Tracking</span>
                  </div>
                  {(liveLocation?.updated_at || delivery.location_updated_at) && (
                    <span className="text-slate-500 text-xs">
                      Updated {formatDate(liveLocation?.updated_at || delivery.location_updated_at)}
                    </span>
                  )}
                </div>
                <Suspense fallback={<div className="h-[280px] bg-white/5 rounded-xl flex items-center justify-center text-slate-500">Loading map...</div>}>
                  <DeliveryMap
                    driverLat={liveLocation?.lat || delivery.driver_lat}
                    driverLng={liveLocation?.lng || delivery.driver_lng}
                    locationUpdatedAt={liveLocation?.updated_at || delivery.location_updated_at}
                    height="280px"
                  />
                </Suspense>
              </div>
            )}

            {/* Notes */}
            {delivery.notes && (
              <div className="bg-brand-surface rounded-2xl border border-white/10 p-6 mb-6" data-testid="notes-card">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-400 text-sm font-medium">Delivery Notes</span>
                </div>
                <p className="text-slate-300">{delivery.notes}</p>
              </div>
            )}

            {/* Proof of Delivery */}
            {delivery.status === 'delivered' && (delivery.pod_photo_url || delivery.pod_signature_url) && (
              <div className="bg-brand-surface rounded-2xl border border-green-500/20 p-6 mb-6" data-testid="pod-card">
                <div className="flex items-center gap-2 mb-4">
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 text-sm font-medium">Proof of Delivery</span>
                  {delivery.pod_timestamp && (
                    <span className="text-slate-500 text-xs ml-auto">{formatDate(delivery.pod_timestamp)}</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {delivery.pod_photo_url && (
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider mb-2 block">Delivery Photo</span>
                      <img
                        src={delivery.pod_photo_url}
                        alt="Proof of delivery"
                        className="w-full rounded-xl object-cover max-h-64 border border-white/10"
                        data-testid="pod-photo"
                      />
                    </div>
                  )}
                  {delivery.pod_signature_url && (
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider mb-2 block">Signature</span>
                      <img
                        src={delivery.pod_signature_url}
                        alt="Customer signature"
                        className="w-full rounded-xl bg-white p-2 max-h-48 object-contain border border-white/10"
                        data-testid="pod-signature"
                      />
                    </div>
                  )}
                </div>
                {delivery.pod_notes && (
                  <p className="text-slate-400 text-sm mt-3">{delivery.pod_notes}</p>
                )}
              </div>
            )}

            {/* Status History Timeline */}
            {delivery.status_history && delivery.status_history.length > 0 && (
              <div className="bg-brand-surface rounded-2xl border border-white/10 p-6" data-testid="status-history">
                <h3 className="font-heading text-lg text-white mb-6">STATUS HISTORY</h3>
                <div className="space-y-0">
                  {[...delivery.status_history].reverse().map((entry, i) => {
                    const stageInfo = STAGES.find(s => s.key === entry.status);
                    const Icon = stageInfo?.icon || Clock;
                    return (
                      <div key={i} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            i === 0 ? 'bg-brand-red text-white' : 'bg-white/10 text-slate-400'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          {i < delivery.status_history.length - 1 && (
                            <div className="w-px h-8 bg-white/10" />
                          )}
                        </div>
                        <div className="pb-6">
                          <p className={`font-medium ${i === 0 ? 'text-white' : 'text-slate-400'}`}>
                            {stageInfo?.label || entry.status}
                          </p>
                          <p className="text-slate-500 text-xs mt-0.5">{formatDate(entry.timestamp)}</p>
                          {entry.note && <p className="text-slate-400 text-sm mt-1">{entry.note}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </section>
      )}

      {/* Info section when no results */}
      {!delivery && !loading && !error && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Search, title: 'Enter Your Job ID', desc: 'Use the Job ID provided in your booking confirmation email.' },
                { icon: Phone, title: 'Verify Identity', desc: 'Enter the phone number or last name used when booking.' },
                { icon: Truck, title: 'Real-Time Updates', desc: 'See live status, ETA countdown, and driver information.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="bg-brand-surface rounded-2xl border border-white/10 p-6 text-center"
                >
                  <item.icon className="w-8 h-8 text-brand-red mx-auto mb-4" />
                  <h3 className="font-heading text-base text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TrackDelivery;
